<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@blueprint/frontend-basics'

import ShopCreation from '@/components/organisms/ShopCreation.vue'
import type { useServiceStore } from '@/stores/service.store.ts'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const serviceStore = inject<ReturnType<typeof useServiceStore>>('serviceStore')!

const pageTitle = ref(t('home.welcome'))

const initialShopName = computed(() => {
  const value = route.query.prefillShopName
  return typeof value === 'string' ? value : ''
})

const autoCreate = computed(() => {
  const value = route.query.autoCreateShop
  if (typeof value === 'string') {
    return value === '1' || value.toLowerCase() === 'true'
  }
  return false
})

onMounted(async () => {
  await userStore.awaitInit()
})

const handleSignupRequired = async ({
  shopName,
}: {
  shopName: string
}): Promise<void> => {
  const query = {
    ...route.query,
    prefillShopName: shopName,
    autoCreateShop: '1',
  }
  const resolved = router.resolve({
    name: route.name as string,
    params: route.params,
    query,
  })
  const redirectUrl = new URL(resolved.href, window.location.origin).toString()
  await serviceStore.authService.startSignupProcess(redirectUrl, '')
}
</script>

<template>
  <v-container class="py-8" max-width="560">
    <v-card variant="elevated" rounded="lg">
      <v-card-title class="text-h5">{{ pageTitle }}</v-card-title>
      <v-card-text>
        <ShopCreation
          :initial-shop-name="initialShopName"
          :auto-create="autoCreate"
          @title-change="pageTitle = $event"
          @signup-required="handleSignupRequired"
        />
      </v-card-text>
    </v-card>
  </v-container>
</template>
